import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, CalendarDays, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cls = "w-full h-11 px-3.5 rounded-lg border border-neutral-300 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/20 focus:border-neutral-500";

export default function PublicRegistration() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState({ name: "", email: "", phone: "", stand_wish: "", note: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const v = (k, val) => setValues((s) => ({ ...s, [k]: val }));

  useEffect(() => {
    axios.get(`${API}/public/reg/${slug}`)
      .then(({ data }) => setForm(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/public/reg/${slug}`, values);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Etwas ist schiefgelaufen — bitte erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <Shell><p className="text-neutral-500 text-center py-10">Dieses Formular existiert nicht (mehr).</p></Shell>
    );
  }
  if (!form) {
    return <Shell><div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div></Shell>;
  }

  return (
    <Shell senderName={form.sender_name}>
      <h1 className="font-head text-2xl font-bold text-neutral-900">{form.title}</h1>
      {form.description && <p className="text-[15px] text-neutral-600 mt-2 leading-relaxed whitespace-pre-line">{form.description}</p>}
      {form.deadline && (
        <p className="text-[13px] text-neutral-400 mt-2 flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4" /> Anmeldeschluss: {new Date(form.deadline).toLocaleDateString("de-DE")}
        </p>
      )}

      {done ? (
        <div data-testid="public-reg-success" className="mt-8 rounded-xl bg-emerald-50 border border-emerald-200 p-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
          <h2 className="font-head font-bold text-emerald-800 text-lg mt-3">Anmeldung erhalten!</h2>
          <p className="text-sm text-emerald-700 mt-1">
            Danke, {values.name.split(" ")[0]}! Du bekommst in wenigen Minuten eine Bestätigungs-E-Mail.
            Wir melden uns, sobald deine Anmeldung geprüft wurde.
          </p>
        </div>
      ) : !form.open ? (
        <div data-testid="public-reg-closed" className="mt-8 rounded-xl bg-amber-50 border border-amber-200 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-amber-500 mx-auto" />
          <h2 className="font-head font-bold text-amber-800 text-lg mt-3">Ausgebucht</h2>
          <p className="text-sm text-amber-700 mt-1">Alle Plätze sind vergeben bzw. die Anmeldung ist geschlossen. Danke für dein Interesse!</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <Field label="Name *">
            <input data-testid="public-reg-name" required value={values.name} onChange={(e) => v("name", e.target.value)} placeholder="Vor- und Nachname" className={cls} />
          </Field>
          <Field label="E-Mail *">
            <input data-testid="public-reg-email" required type="email" value={values.email} onChange={(e) => v("email", e.target.value)} placeholder="du@beispiel.de" className={cls} />
          </Field>
          <Field label="Telefon">
            <input data-testid="public-reg-phone" value={values.phone} onChange={(e) => v("phone", e.target.value)} placeholder="Für Rückfragen" className={cls} />
          </Field>
          {form.stand_options?.length > 0 && (
            <Field label="Standwunsch">
              <select data-testid="public-reg-stand" value={values.stand_wish} onChange={(e) => v("stand_wish", e.target.value)} className={cls}>
                <option value="">Bitte wählen…</option>
                {form.stand_options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          )}
          <Field label="Bemerkung">
            <textarea data-testid="public-reg-note" rows={3} value={values.note} onChange={(e) => v("note", e.target.value)} placeholder="Was möchtest du uns noch mitteilen?" className={`${cls} h-auto py-2.5 resize-none`} />
          </Field>
          {error && <p data-testid="public-reg-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">{error}</p>}
          <button
            data-testid="public-reg-submit"
            type="submit"
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 text-white text-[15px] font-semibold px-5 py-3.5 rounded-xl hover:bg-neutral-800 disabled:opacity-60"
          >
            {sending && <Loader2 className="h-4 w-4 animate-spin" />} Verbindlich anmelden
          </button>
          <p className="text-[11px] text-neutral-400 text-center">Deine Daten werden ausschließlich zur Organisation dieser Veranstaltung verwendet.</p>
        </form>
      )}
    </Shell>
  );
}

function Shell({ children, senderName }) {
  return (
    <div className="min-h-screen bg-neutral-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-neutral-900 text-white rounded-t-2xl px-7 py-5">
          <span className="font-head font-bold tracking-wide text-[17px]">{senderName || "Anmeldung"}</span>
        </div>
        <div className="bg-white rounded-b-2xl border border-neutral-200 border-t-0 px-7 py-7 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[13px] font-semibold text-neutral-700">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
